import { c as _c } from "react/compiler-runtime";
import { useContext, useState } from 'react';
import useSWR from 'swr';
import { Transformation } from '../../../../../../../typings/Transformation.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import field_formats from '../../../../utils/field-formatters';
import Badge from '../../../common/base/Badge';
import Button from '../../../common/base/Button';
import { StyledCard } from '../../../common/base/Card';
import Link from '../../../common/base/Link';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import { CollapsableDataSection } from '../../../common/Data/CollapsableDataSection';
import { useDialog } from '../../../common/Dialog';
import TextInput from '../../../common/Form/Fields/TextInput';
import { Div } from '../../../common/helpers/StyledUtils';
import { useToasts } from '../../../common/Toast';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { StyledViewSplitPaneContent, StyledViewSplitPaneLayout, StyledViewSplitPaneSection, StyledViewSplitPaneSidebar, StyledViewSplitPaneSidebarBody, ViewMetadataCopyable, ViewMetadataDate, ViewMetadataItem, ViewSplitPaneSidebarHeader, ViewSplitPaneSidebarSection } from '../../../layouts/ViewLayout';
import { DashboardContext } from '../DashboardContext';
import { CodeBlock } from '../Event/CodeBlock';
const json_to_keyval = (obj: object, hide_value = true) => {
  return Object.entries(obj).map(([key, value]) => `${key}: ${hide_value ? '*'.repeat(String(value).length) : value}`).join('\n');
};
interface TransformationOverviewProps {
  transformation: Transformation;
  disable_delete: boolean;
  onDelete: () => void;
}
export const TransformationOverview = t0 => {
  const $ = _c(98);
  const {
    transformation,
    disable_delete,
    onDelete
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    is_degraded_banner_active
  } = useContext(DashboardContext);
  let t1;
  if ($[0] !== transformation.id) {
    t1 = APIMethodKeys.transformations.get(transformation.id);
    $[0] = transformation.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== HookdeckAPI.transformations || $[3] !== transformation.id) {
    t2 = () => HookdeckAPI.transformations.get(transformation.id);
    $[2] = HookdeckAPI.transformations;
    $[3] = transformation.id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    mutate
  } = useSWR(t1, t2);
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  let t3;
  if ($[5] !== HookdeckAPI.transformations || $[6] !== addToast || $[7] !== mutate || $[8] !== showDialog || $[9] !== transformation.id || $[10] !== transformation.name) {
    t3 = () => {
      showDialog(async values => {
        ;
        try {
          const new_transformation = await HookdeckAPI.transformations.update(transformation.id, {
            name: values.name
          });
          addToast("success", "Transformation name updated");
          mutate(new_transformation);
          return true;
        } catch (t4) {
          addToast("error", "Failed to update transformation name");
          return false;
        }
      }, _temp, {
        title: "Edit transformation name",
        submit_label: "Save",
        cancel_label: "Cancel",
        form_props: {
          initial_values: {
            name: transformation.name || ""
          },
          validate: async t5 => {
            const {
              name
            } = t5;
            if (!name || name.length < 0) {
              return {
                name: "Required"
              };
            } else {
              if (name !== transformation.name) {
                const used = await HookdeckAPI.transformations.nameIsUsed(name);
                if (used) {
                  return {
                    name: "Name is already used."
                  };
                }
              }
            }
            return {};
          },
          Fields: () => <TextInput format={field_formats.slugify} name="name" placeholder="Name" m={0} help="Provide a unique name that represents this transformation." mono={true} required={true} validate={async name_0 => {
            if (!name_0) {
              return "Required";
            } else {
              const name_changed = name_0 !== transformation.name;
              if (name_changed) {
                const transformations = await HookdeckAPI.transformations.list({
                  name: name_0,
                  limit: 1
                });
                if (transformations.count > 0 && (!transformation.id || transformations.models[0]?.id !== transformation.id)) {
                  return "Name is already used.";
                }
              }
            }
          }} />
        }
      });
    };
    $[5] = HookdeckAPI.transformations;
    $[6] = addToast;
    $[7] = mutate;
    $[8] = showDialog;
    $[9] = transformation.id;
    $[10] = transformation.name;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const showNameDialog = t3;
  const [collapse_code, setCollapseCode] = useState(false);
  const [collapse_env, setCollapseEnv] = useState(false);
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ViewSplitPaneSidebarHeader title="Transformation details" />;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = !disable_delete;
  const t6 = disable_delete ? "In use" : "Not used";
  let t7;
  if ($[13] !== disable_delete || $[14] !== t5 || $[15] !== t6) {
    t7 = <ViewMetadataItem label="Status"><Tooltip tooltip="Indicates if this transformation is currently acting on events. Transformations in use cannot be deleted."><Badge neutral={t5} success={disable_delete} subtle={true}>{t6}</Badge></Tooltip></ViewMetadataItem>;
    $[13] = disable_delete;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      align: "center",
      gap: 2
    };
    t9 = {
      minWidth: 0
    };
    $[17] = t8;
    $[18] = t9;
  } else {
    t8 = $[17];
    t9 = $[18];
  }
  let t10;
  if ($[19] !== transformation.name) {
    t10 = <Text mono={true} ellipsis={true} title={transformation.name}>{transformation.name}</Text>;
    $[19] = transformation.name;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== showNameDialog) {
    t11 = <Button ghost={true} small={true} icon="edit" onClick={() => showNameDialog()} />;
    $[21] = showNameDialog;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11) {
    t12 = <ViewMetadataItem label="Name"><Div flex={t8} style={t9}>{t10}{t11}</Div></ViewMetadataItem>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t7) {
    t13 = <ViewSplitPaneSidebarSection>{t7}{t12}</ViewSplitPaneSidebarSection>;
    $[26] = t12;
    $[27] = t7;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== transformation.id) {
    t14 = <ViewMetadataItem label="Transformation ID"><ViewMetadataCopyable value={transformation.id} /></ViewMetadataItem>;
    $[29] = transformation.id;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== transformation.created_at) {
    t15 = <ViewMetadataItem label="Created at"><ViewMetadataDate date={transformation.created_at} /></ViewMetadataItem>;
    $[31] = transformation.created_at;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== transformation.updated_at) {
    t16 = <ViewMetadataItem label="Last updated"><ViewMetadataDate date={transformation.updated_at} /></ViewMetadataItem>;
    $[33] = transformation.updated_at;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t14 || $[36] !== t15 || $[37] !== t16) {
    t17 = <ViewSplitPaneSidebarSection title="Metadata" bordered={true}>{t14}{t15}{t16}</ViewSplitPaneSidebarSection>;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t13 || $[40] !== t17) {
    t18 = <StyledViewSplitPaneSidebar><StyledViewSplitPaneSidebarBody>{t4}{t13}{t17}</StyledViewSplitPaneSidebarBody></StyledViewSplitPaneSidebar>;
    $[39] = t13;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== collapse_code) {
    t19 = <Text as="h2" size="l" bold={true} m={0} onClick={() => setCollapseCode(!collapse_code)}>Transformation code</Text>;
    $[42] = collapse_code;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  const t20 = collapse_code ? "chevron_down" : "chevron_up";
  let t21;
  if ($[44] !== collapse_code) {
    t21 = () => setCollapseCode(!collapse_code);
    $[44] = collapse_code;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t20 || $[47] !== t21) {
    t22 = <Link neutral={true} icon={t20} onClick={t21} />;
    $[46] = t20;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== collapse_code || $[50] !== t19 || $[51] !== t22) {
    t23 = <CollapsableDataSection embedded={false} collapsed={collapse_code}>{t19}{t22}</CollapsableDataSection>;
    $[49] = collapse_code;
    $[50] = t19;
    $[51] = t22;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== collapse_code || $[54] !== transformation.code) {
    t24 = !collapse_code && <CodeBlock.Provider text={transformation.code} lang="javascript"><CodeBlock.Code /></CodeBlock.Provider>;
    $[53] = collapse_code;
    $[54] = transformation.code;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t23 || $[57] !== t24) {
    t25 = <StyledViewSplitPaneSection>{t23}{t24}</StyledViewSplitPaneSection>;
    $[56] = t23;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== collapse_env) {
    t26 = <Text as="h2" size="l" bold={true} m={0} onClick={() => setCollapseEnv(!collapse_env)}>Environment variables</Text>;
    $[59] = collapse_env;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== transformation.env) {
    t27 = Object.keys(transformation.env || {});
    $[61] = transformation.env;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t27.length) {
    t28 = <Badge neutral={true}>{t27.length}</Badge>;
    $[63] = t27.length;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  const t29 = collapse_env ? "chevron_down" : "chevron_up";
  let t30;
  if ($[65] !== collapse_env) {
    t30 = () => setCollapseEnv(!collapse_env);
    $[65] = collapse_env;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== t29 || $[68] !== t30) {
    t31 = <Link neutral={true} icon={t29} onClick={t30} />;
    $[67] = t29;
    $[68] = t30;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] !== collapse_env || $[71] !== t26 || $[72] !== t28 || $[73] !== t31) {
    t32 = <CollapsableDataSection embedded={false} collapsed={collapse_env}>{t26}{t28}{t31}</CollapsableDataSection>;
    $[70] = collapse_env;
    $[71] = t26;
    $[72] = t28;
    $[73] = t31;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== collapse_env || $[76] !== transformation.env) {
    t33 = !collapse_env && (transformation.env && Object.keys(transformation.env).length > 0 ? <CodeBlock.Provider text={json_to_keyval(transformation.env || {})} lang="text" custom_search_fn={_temp2}><CodeBlock.Code /></CodeBlock.Provider> : <StyledCard flex={{
      align: "center",
      justify: "center"
    }} p={{
      y: 6
    }}><Text muted={true}>No environment variables.</Text></StyledCard>);
    $[75] = collapse_env;
    $[76] = transformation.env;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] !== t32 || $[79] !== t33) {
    t34 = <StyledViewSplitPaneSection>{t32}{t33}</StyledViewSplitPaneSection>;
    $[78] = t32;
    $[79] = t33;
    $[80] = t34;
  } else {
    t34 = $[80];
  }
  let t35;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <Text as="h2" size="l" bold={true} m={0}>Delete transformation</Text>;
    $[81] = t35;
  } else {
    t35 = $[81];
  }
  let t36;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <Text as="p" muted={true} m={{
      b: 4
    }}>Permanently deletes this transformation. Transformations can only be deleted if they are not currently in use on a connection.</Text>;
    $[82] = t36;
  } else {
    t36 = $[82];
  }
  const t37 = !disable_delete;
  const t38 = disable_delete ? "This transformation is currently in use on a connection." : undefined;
  let t39;
  if ($[83] !== disable_delete || $[84] !== onDelete) {
    t39 = <Button.Permission disabled={disable_delete} outline={true} danger={true} onClick={onDelete} icon="delete">Delete transformation</Button.Permission>;
    $[83] = disable_delete;
    $[84] = onDelete;
    $[85] = t39;
  } else {
    t39 = $[85];
  }
  let t40;
  if ($[86] !== t37 || $[87] !== t38 || $[88] !== t39) {
    t40 = <StyledViewSplitPaneSection max_width="medium">{t35}{t36}<Tooltip side="bottom" disabled={t37} tooltip={t38}>{t39}</Tooltip></StyledViewSplitPaneSection>;
    $[86] = t37;
    $[87] = t38;
    $[88] = t39;
    $[89] = t40;
  } else {
    t40 = $[89];
  }
  let t41;
  if ($[90] !== t25 || $[91] !== t34 || $[92] !== t40) {
    t41 = <StyledViewSplitPaneContent>{t25}{t34}{t40}</StyledViewSplitPaneContent>;
    $[90] = t25;
    $[91] = t34;
    $[92] = t40;
    $[93] = t41;
  } else {
    t41 = $[93];
  }
  let t42;
  if ($[94] !== is_degraded_banner_active || $[95] !== t18 || $[96] !== t41) {
    t42 = <StyledViewSplitPaneLayout banner_active={is_degraded_banner_active}>{t18}{t41}</StyledViewSplitPaneLayout>;
    $[94] = is_degraded_banner_active;
    $[95] = t18;
    $[96] = t41;
    $[97] = t42;
  } else {
    t42 = $[97];
  }
  return t42;
};
function _temp() {
  return null;
}
function _temp2(text, search_value) {
  return (text as string).split("\n").filter(line => line.includes(search_value)).join("\n");
}